import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Undo2 } from "lucide-react";
import { formatCurrency, API_URL } from "../../lib/utils";

const creditedOf = (invoice, item, idx) => Number((invoice.credited_quantities || {})[item.item_id || `${item.product_id}#${idx}`] || 0);
const remainingOf = (invoice, item, idx) => Math.max(0, Number(item.quantity || 0) - creditedOf(invoice, item, idx));

export const CreditNoteDialog = ({ invoice, open, onClose, headers, onDone }) => {
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) {
      setReason("");
      setQty(Object.fromEntries((invoice.items || []).map((it, i) => [it.item_id || String(i), remainingOf(invoice, it, i)])));
    }
  }, [invoice]);

  if (!invoice) return null;
  const items = invoice.items || [];
  const keyOf = (it, i) => it.item_id || String(i);
  const total = items.reduce((s, it, i) => {
    const q = Number(qty[keyOf(it, i)] || 0);
    const unit = Number(it.line_total ?? it.unit_price * it.quantity) / Math.max(Number(it.quantity) || 1, 1e-9);
    return s + q * unit;
  }, 0);
  const isFull = items.every((it, i) => Number(qty[keyOf(it, i)] || 0) === remainingOf(invoice, it, i));

  const submit = async () => {
    if (!reason.trim()) return toast.error("Indique o motivo da rectificação");
    const lines = items.map((it, i) => ({ item_id: it.item_id, product_id: it.product_id, quantity: Number(qty[keyOf(it, i)] || 0) })).filter((l) => l.quantity > 0);
    if (!lines.length) return toast.error("Indique pelo menos uma quantidade a creditar");
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/invoices/${invoice.invoice_id}/credit-note`, { reason, lines: isFull ? null : lines }, { headers, withCredentials: true });
      const restocked = r.data.restocked || [];
      toast.success(`Nota de crédito ${r.data.credit_note.invoice_number} emitida${restocked.length ? ` — stock reposto: ${restocked.join(", ")}` : ""}`);
      onDone?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao emitir nota de crédito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="credit-note-modal">
        <DialogHeader>
          <DialogTitle>Nota de Crédito — {invoice.invoice_number}</DialogTitle>
          <DialogDescription>Rectificação total ou parcial. A NC é assinada e referencia a factura; nunca se pode creditar mais do que foi facturado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2 max-h-64 overflow-auto pr-1">
            {items.map((it, i) => {
              const k = keyOf(it, i);
              const rem = remainingOf(invoice, it, i);
              return (
                <div key={k} className="flex items-center gap-2 text-sm" data-testid={`nc-line-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{it.product_name}</p>
                    <p className="text-xs text-muted-foreground">facturado {it.quantity} · já creditado {creditedOf(invoice, it, i)} · {formatCurrency(it.unit_price)}</p>
                  </div>
                  <Input type="number" min="0" max={rem} step="1" value={qty[k] ?? 0} disabled={rem === 0} onChange={(e) => setQty({ ...qty, [k]: Math.min(rem, Math.max(0, Number(e.target.value))) })} className="w-20 text-right" data-testid={`nc-qty-${i}`} />
                </div>
              );
            })}
          </div>
          <div className="space-y-1">
            <Label>Motivo da rectificação *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Produto danificado, erro de facturação" data-testid="nc-reason-input" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground" data-testid="nc-restock-info">Artigos com stock são repostos automaticamente.</span>
            <span className="font-mono font-bold" data-testid="nc-total">{formatCurrency(total)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="confirm-credit-note-btn"><Undo2 className="w-4 h-4 mr-2" /> Emitir NC {isFull ? "total" : "parcial"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
